import React, { useState } from 'react';
import { T, card, definitionRow, RISK_DOT, NAV_DOT } from './accountStyles';
import { utcHhmmToLocal, localToUtcHhmm, formatUtcHhmmLocal } from '../../utils/timezone';
import { createHabLightSettings, updateHabLightSettings } from '../../services/habLightService';

// Defaults for a buoy with no settings row yet (first save is a POST).
const DEFAULTS = { intensity: 50, hab_mode: true, nav_color_level: 4, mode: 'at_night', utc_on: 2300, utc_off: 200 };

// Night-light schedule bounds (grid-local): the light may turn on no earlier
// than 4:00 PM (dusk) and turn off no later than 6:00 AM. "HH:MM" strings are
// zero-padded so lexical comparison matches chronological order.
const MIN_ON = '16:00';
const MAX_OFF = '06:00';
const clampOn = (t) => (t && t < MIN_ON ? MIN_ON : t);
const clampOff = (t) => (t && t > MAX_OFF ? MAX_OFF : t);

const pad = (n) => String(n).padStart(2, '0');

const modeLabel = (mode, settings, timezone) => {
  if (mode === 'always_on') return 'Always on';
  if (mode === 'at_night') return 'On at night only';
  if (mode === 'window') {
    const on = formatUtcHhmmLocal(settings.utc_on ?? DEFAULTS.utc_on, timezone);
    const off = formatUtcHhmmLocal(settings.utc_off ?? DEFAULTS.utc_off, timezone);
    return `Custom: ${on}–${off}`;
  }
  return '—';
};

const lightModeText = (s) =>
  s.hab_mode
    ? 'Color-coded by cyanobacteria risk'
    : `Navigation safety light (${s.nav_color_level === 5 ? 'flashing' : 'steady'} white)`;

const Dot = ({ color, size = 9 }) => (
  <span style={{ display: 'inline-block', width: size, height: size, borderRadius: '50%', background: color, flexShrink: 0 }} />
);

const label = { fontSize: 15, color: T.textMuted };
const value = { fontSize: 16, fontWeight: 600, color: T.textPrimary };
const selectStyle = { fontSize: 16, padding: '13px 14px', borderRadius: 10, border: `1px solid ${T.cardBorder}`, background: '#fff', width: '100%', fontFamily: T.body, color: T.textPrimary };
const caption = { fontSize: 13.5, color: '#8a8f86', margin: '6px 0 0' };

export default function BuoyLightCard({ buoy, timezone, isOwner, token, onSaved, onToast }) {
  // A freshly-deployed buoy has no settings row yet (GET returned []). Until an
  // owner sets initial values (first save = POST), we must NOT present the
  // defaults as if they were real, configured values.
  const configured = buoy.settings != null;
  const s = buoy.settings || DEFAULTS;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const startEdit = () => {
    const on = utcHhmmToLocal(s.utc_on ?? DEFAULTS.utc_on, timezone);
    const off = utcHhmmToLocal(s.utc_off ?? DEFAULTS.utc_off, timezone);
    setDraft({
      intensity: s.intensity ?? DEFAULTS.intensity,
      hab_mode: s.hab_mode ?? DEFAULTS.hab_mode,
      nav_color_level: s.nav_color_level ?? DEFAULTS.nav_color_level,
      mode: s.mode ?? DEFAULTS.mode,
      onStr: clampOn(`${pad(on.h)}:${pad(on.m)}`),
      offStr: clampOff(`${pad(off.h)}:${pad(off.m)}`),
    });
    setErr(null);
    setEditing(true);
  };

  const cancel = () => { setEditing(false); setDraft(null); setErr(null); };

  const save = async () => {
    if (draft.mode === 'window') {
      if (!draft.onStr || !draft.offStr) {
        setErr('Set both a turn-on and turn-off time for custom hours.');
        return;
      }
      if (draft.onStr < MIN_ON) {
        setErr('The light can turn on no earlier than 4:00 PM.');
        return;
      }
      if (draft.offStr > MAX_OFF) {
        setErr('The light can turn off no later than 6:00 AM.');
        return;
      }
    }
    setSaving(true);
    setErr(null);
    const payload = {
      intensity: Math.round(draft.intensity),
      hab_mode: draft.hab_mode,
      nav_color_level: draft.nav_color_level,
      mode: draft.mode,
    };
    if (draft.mode === 'window') {
      const [onH, onM] = draft.onStr.split(':').map(Number);
      const [offH, offM] = draft.offStr.split(':').map(Number);
      payload.utc_on = localToUtcHhmm(onH, onM, timezone);
      payload.utc_off = localToUtcHhmm(offH, offM, timezone);
    }
    try {
      const row = buoy.settingsId
        ? await updateHabLightSettings(buoy.settingsId, payload, token)
        : await createHabLightSettings({ buoy: buoy.buoyId, ...payload }, token);
      onSaved(buoy.buoyId, { settingsId: row.id, settings: row });
      setEditing(false);
      setDraft(null);
      onToast('Light settings saved for this buoy');
    } catch (e) {
      setErr(e.message || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Header ──────────────────────────────────────────────────────────────
  const header = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <Dot color={T.greenDot} />
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.textHeading }}>{buoy.buoyName}</div>
          <div style={{ fontSize: 13, color: T.textMuted }}>Active</div>
        </div>
      </div>
      {editing ? (
        <span style={{ fontSize: 13, fontWeight: 700, color: T.amberText, background: T.amberBg, padding: '4px 11px', borderRadius: 999 }}>✎ Editing</span>
      ) : !isOwner ? (
        <span style={{ fontSize: 13, fontWeight: 600, color: T.greyPillText, background: T.greyPillBg, padding: '4px 11px', borderRadius: 999 }}>🔒 View only</span>
      ) : !configured ? (
        <span style={{ fontSize: 13, fontWeight: 700, color: T.amberText, background: T.amberBg, padding: '4px 11px', borderRadius: 999 }}>Setup needed</span>
      ) : null}
    </div>
  );

  // ── Not-yet-configured message (buoy has no settings row) ────────────────
  const notConfigured = (
    <div style={{ marginTop: 18, padding: '18px 0', borderTop: `1px solid ${T.divider}` }}>
      <p style={{ fontSize: 15, fontWeight: 600, color: T.textPrimary, margin: '0 0 4px' }}>
        Light settings not yet configured
      </p>
      <p style={{ fontSize: 14, color: T.textMuted, margin: 0 }}>
        {isOwner
          ? 'This buoy doesn’t have light settings yet. Set your initial settings to get started.'
          : 'This buoy doesn’t have light settings yet. Ask a grid administrator to configure them.'}
      </p>
    </div>
  );

  // ── Summary (locked / read-only) ────────────────────────────────────────
  const summary = (
    <div style={{ marginTop: 18 }}>
      <div style={definitionRow}>
        <span style={label}>Brightness</span>
        <span style={value}>{s.intensity ?? DEFAULTS.intensity}%</span>
      </div>
      <div style={definitionRow}>
        <span style={label}>Light mode</span>
        <span style={{ ...value, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Dot color={s.hab_mode ? RISK_DOT.BASELINE : NAV_DOT} size={10} />
          {lightModeText(s)}
        </span>
      </div>
      <div style={definitionRow}>
        <span style={label}>Schedule</span>
        <span style={value}>{modeLabel(s.mode ?? DEFAULTS.mode, s, timezone)}</span>
      </div>
    </div>
  );

  // ── Editing form ────────────────────────────────────────────────────────
  const set = (patch) => setDraft(d => ({ ...d, ...patch }));
  const form = draft && (
    <div style={{ marginTop: 18 }}>
      {/* Brightness */}
      <div style={{ padding: '15px 0', borderTop: `1px solid ${T.divider}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={label}>Brightness</span>
          <span style={{ fontSize: 16, fontWeight: 700, color: T.green }}>{Math.round(draft.intensity)}%</span>
        </div>
        <input
          type="range" min={0} max={100} value={draft.intensity}
          onChange={(e) => set({ intensity: Number(e.target.value) })}
          style={{ width: '100%', accentColor: T.green, height: 8, marginTop: 12 }}
        />
        <p style={caption}>0% turns the light off.</p>
      </div>

      {/* Light mode */}
      <div style={{ padding: '15px 0', borderTop: `1px solid ${T.divider}` }}>
        <span style={{ ...label, display: 'block', marginBottom: 8 }}>Light mode</span>
        <select
          value={draft.hab_mode ? 'hab' : 'nav'}
          onChange={(e) => set({ hab_mode: e.target.value === 'hab' })}
          style={selectStyle}
        >
          <option value="hab">Color-coded by cyanobacteria risk</option>
          <option value="nav">Navigation safety light (steady white)</option>
        </select>
        {!draft.hab_mode && (
          <div style={{ marginTop: 12 }}>
            <span style={{ ...label, display: 'block', marginBottom: 8 }}>Light pattern</span>
            <select
              value={draft.nav_color_level}
              onChange={(e) => set({ nav_color_level: Number(e.target.value) })}
              style={selectStyle}
            >
              <option value={4}>Steady white</option>
              <option value={5}>Flashing white</option>
            </select>
          </div>
        )}
        <p style={caption}>
          {draft.hab_mode
            ? 'The light color is set automatically by the cyanobacteria risk level.'
            : 'A plain white navigation light — not tied to cyanobacteria risk.'}
        </p>
      </div>

      {/* Schedule */}
      <div style={{ padding: '15px 0', borderTop: `1px solid ${T.divider}` }}>
        <span style={{ ...label, display: 'block', marginBottom: 8 }}>Schedule</span>
        <select value={draft.mode} onChange={(e) => set({ mode: e.target.value })} style={selectStyle}>
          <option value="always_on">Always on</option>
          <option value="at_night">On at night only</option>
          <option value="window">Custom hours</option>
        </select>
        {draft.mode === 'at_night' && (
          <p style={caption}>Turns on automatically at night and stays off during the day.</p>
        )}
        {draft.mode === 'window' && (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12 }}>
            <label style={{ flex: 1, minWidth: 130 }}>
              <span style={{ ...label, display: 'block', marginBottom: 6 }}>Turns on</span>
              <input type="time" min={MIN_ON} value={draft.onStr} onChange={(e) => set({ onStr: e.target.value })} style={selectStyle} />
            </label>
            <label style={{ flex: 1, minWidth: 130 }}>
              <span style={{ ...label, display: 'block', marginBottom: 6 }}>Turns off</span>
              <input type="time" max={MAX_OFF} value={draft.offStr} onChange={(e) => set({ offStr: e.target.value })} style={selectStyle} />
            </label>
          </div>
        )}
        {draft.mode === 'window' && (
          <p style={caption}>
            Times are in this grid's local time ({timezone}). The light can turn on no earlier than 4:00 PM and turn off no later than 6:00 AM.
          </p>
        )}
      </div>
    </div>
  );

  // ── Footer ──────────────────────────────────────────────────────────────
  const footer = (() => {
    if (editing) {
      return (
        <div style={{ borderTop: `1px solid ${T.divider}`, paddingTop: 20, marginTop: 6 }}>
          {err && <p style={{ color: T.error, fontSize: 13.5, margin: '0 0 12px' }}>{err}</p>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={cancel} disabled={saving} style={{ background: '#fff', border: `1.5px solid #d9d3c5`, color: T.textMuted, borderRadius: 10, padding: '11px 20px', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: T.body }}>
              Cancel
            </button>
            <button onClick={save} disabled={saving} style={{ background: T.green, border: 'none', color: '#fff', borderRadius: 10, padding: '11px 22px', fontSize: 15, fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1, fontFamily: T.body }}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      );
    }
    // Non-owner: read-only. The unconfigured case already explains itself in the
    // body, so only show the view-access note once a configured row exists.
    if (!isOwner) {
      if (!configured) return null;
      return (
        <div style={{ borderTop: `1px solid ${T.divider}`, paddingTop: 20, marginTop: 6 }}>
          <span style={{ fontSize: 13.5, color: '#8a8f86' }}>
            You have view access to this grid. To change light settings, ask a grid administrator or request administrator access.
          </span>
        </div>
      );
    }
    // Owner, no settings row yet → primary call to action to create them.
    if (!configured) {
      return (
        <div style={{ borderTop: `1px solid ${T.divider}`, paddingTop: 20, marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13.5, color: '#8a8f86' }}>Please set this buoy's initial light settings.</span>
          <button onClick={startEdit} style={{ background: T.green, border: 'none', color: '#fff', borderRadius: 10, padding: '11px 22px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: T.body }}>
            Set initial settings
          </button>
        </div>
      );
    }
    // Owner, configured, locked.
    return (
      <div style={{ borderTop: `1px solid ${T.divider}`, paddingTop: 20, marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13.5, color: '#8a8f86' }}>🔒 Settings are locked to prevent accidental changes.</span>
        <button onClick={startEdit} style={{ background: '#fff', border: `1.5px solid ${T.green}`, color: T.green, borderRadius: 10, padding: '11px 22px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: T.body }}>
          ✎ Update settings
        </button>
      </div>
    );
  })();

  return (
    <div style={{ ...card, marginBottom: 14 }}>
      {header}
      {editing ? form : configured ? summary : notConfigured}
      {footer}
    </div>
  );
}
